import { useCallback, useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useTrackedQuery as useQuery } from '@ais-ui/performance-tracking';
import { useBasePath, REFETCH_INTERVALS } from '@ais-ui/custom-hooks';
import {
  Configuration,
  PortApi,
  type PortExtended,
  PortList,
} from '@ais-ui/api-spec/portserver';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';
import { useCredentials } from '@ais-ui/auth';
import { useServiceAvailability } from './ServiceAvailabilityProvider';

// Credentials type for direct injection
export interface PortsApiCredentials {
  username: string;
  password: string;
}

// Core function to create PortApi - can be used with or without hooks
export const createPortsApi = (
  sessionId: string,
  hostname: string,
  credentials?: PortsApiCredentials | null,
): PortApi => {
  let basePath = getBasePath();
  let configuration: Configuration;

  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/portserver`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/portserver`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  return new PortApi(configuration);
};

// Hook version that uses context (for main app)
const usePortsApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  const portserverApi = useMemo(
    () => createPortsApi(sessionId, hostname, credentials),
    [sessionId, hostname, credentials],
  );
  return apiClientWrapper(portserverApi) as PortApi;
};

export const usePortsGetList = (
  pattern = '',
  offset = 0,
  limit = 10000,
): UseQueryResult<Record<'records', Array<PortList>>> => {
  const portserverApi = usePortsApi();
  const { isAvailable } = useServiceAvailability();

  return useQuery(
    {
      queryKey: ['portListGet', pattern, offset, limit],
      queryFn: () => portserverApi.portListGet({ pattern, offset, limit }),
      enabled: isAvailable('portserver'),
    },
    'ports:list',
  );
};

export const usePortFetch = (): ((
  portId: number | string,
) => Promise<PortExtended>) => {
  const portserverApi = usePortsApi();
  const queryClient = useQueryClient();

  const fetchPort = useCallback(
    async (portId: number | string) => {
      try {
        // First check if data exists in cache
        const cachedData = queryClient.getQueryData(['port', portId]);
        if (cachedData) {
          return cachedData;
        }

        // If not in cache, fetch from API
        const data = await portserverApi.portInfoIdGet({ id: String(portId) });

        // Cache the result
        queryClient.setQueryData(['port', portId], data);

        return data;
      } catch (error) {
        console.error('Error fetching port:', error);
        throw error;
      }
    },
    [portserverApi, queryClient],
  );

  return fetchPort;
};

export const usePortGet = (portId: number | string) => {
  const portserverApi = usePortsApi();

  return useQuery(
    {
      queryKey: ['port', portId],
      queryFn: async () => {
        const data = await portserverApi.portInfoIdGet({ id: String(portId) });
        return data;
      },
      enabled: !!portId, // Only fetch if portId is defined
    },
    'ports:get',
  );
};

export const usePortArrivals = (portId?: number) => {
  const portserverApi = usePortsApi();

  return useQuery(
    {
      queryKey: ['arrivals', portId],
      queryFn: async () => {
        if (!portId) {
          throw new Error('Port ID is required but was not provided');
        }

        const data = await portserverApi.portArrivalsIdGet({ id: portId });
        return data;
      },
      enabled: !!portId,
      refetchInterval: REFETCH_INTERVALS.STANDARD,
      refetchIntervalInBackground: true, // Continue polling even when tab is not active
    },
    'ports:arrivals',
  );
};

/**
 * Hook to fetch port calls history for a specific port by locode
 * Similar to useShipPortCallsHistory but for ports instead of vessels
 */
export const usePortCallsHistory = (
  locode: string | undefined,
  dateRange?: { from?: Date; to?: Date },
) => {
  const portserverApi = usePortsApi();

  return useQuery({
    queryKey: ['portCallsHistory', locode, dateRange],
    queryFn: async () => {
      if (!locode) {
        throw new Error('Locode is required');
      }

      // Use provided date range or default to last 90 days
      let endDate: Date;
      let startDate: Date;

      if (dateRange?.from && dateRange?.to) {
        startDate = dateRange.from;
        endDate = dateRange.to;
      } else {
        endDate = new Date();
        startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);
      }

      const result = await portserverApi.portCallsGet({
        locode,
        begin: startDate,
        end: endDate,
      });

      return result;
    },
    enabled: !!locode,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, error: any) => {
      const statusCode =
        error?.response?.parsedBody?.code ||
        error?.response?.status ||
        error?.status;

      if (statusCode === 429) {
        return failureCount < 5;
      }

      if (statusCode && statusCode >= 400 && statusCode < 500) {
        return false;
      }

      return failureCount < 3;
    },
    retryDelay: (attemptIndex, error: any) => {
      const statusCode =
        error?.response?.parsedBody?.code ||
        error?.response?.status ||
        error?.status;

      if (statusCode === 429) {
        return Math.min(2000 * 2 ** attemptIndex, 30000);
      }

      return Math.min(1000 * 2 ** attemptIndex, 10000);
    },
  });
};
